import { DATE_FORMAT, parseTypedDate, stepTypedDate } from '@/utils/typed-date';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { IconButton, InputAdornment, Popover, TextField } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import { type FC, type KeyboardEvent, type ReactNode, useState } from 'react';

const INVALID_DATE_MESSAGE = `Type the date as ${DATE_FORMAT}`;

interface DatePickerProps {
  label: string;
  value?: Dayjs | null | undefined;
  onChange: (date: Dayjs | null) => void;
  name?: string | undefined;
  required?: boolean | undefined;
  error?: boolean | undefined;
  helperText?: ReactNode;
  /** A fixed width in pixels; the field fills its container when omitted. */
  width?: number | undefined;
}

/**
 * Every date field in the app (client review 2026-11-01): a plain text box staff type a date into
 * as numbers, day first ("15/11/2026", "15112026"), with ArrowUp/ArrowDown moving it a day and a
 * calendar button for picking with the mouse. What is typed stays a draft until Enter or leaving
 * the field, so the parent only ever receives a complete, real date: the route planning page
 * reloads its route on every change, and a half-typed date used to send it to the wrong day. A
 * draft that is not a real day is kept, with a hint, rather than committed or thrown away.
 */
const DatePicker: FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  name,
  required,
  error,
  helperText,
  width
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const [isDraftInvalid, setIsDraftInvalid] = useState(false);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);

  const committedText = value?.isValid() ? value.format(DATE_FORMAT) : '';

  const commit = (date: Dayjs | null) => {
    setDraft(null);
    setIsDraftInvalid(false);
    onChange(date);
  };

  const commitDraft = () => {
    if (draft === null) return;
    if (draft.trim() === '') {
      commit(null);
      return;
    }
    const typed = parseTypedDate(draft);
    if (typed) commit(typed);
    else setIsDraftInvalid(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      // Commit, and never let Enter submit a surrounding form with the previous date still in it.
      event.preventDefault();
      commitDraft();
    } else if (event.key === 'Escape') {
      setDraft(null);
      setIsDraftInvalid(false);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const step = event.key === 'ArrowUp' ? 1 : -1;
      commit(stepTypedDate(draft, value, step, dayjs().startOf('day')));
    }
  };

  return (
    <>
      <TextField
        label={label}
        {...(name !== undefined && { name })}
        value={draft ?? committedText}
        placeholder={DATE_FORMAT}
        autoComplete="off"
        onChange={event => {
          setDraft(event.target.value);
          setIsDraftInvalid(false);
        }}
        onBlur={commitDraft}
        onKeyDown={handleKeyDown}
        required={required ?? false}
        error={(error ?? false) || isDraftInvalid}
        helperText={isDraftInvalid ? INVALID_DATE_MESSAGE : helperText}
        fullWidth={width === undefined}
        sx={width === undefined ? {} : { width }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Choose date"
                  edge="end"
                  onClick={event => setCalendarAnchor(event.currentTarget)}
                >
                  <CalendarMonthIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
        }}
      />
      <Popover
        open={calendarAnchor !== null}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <DateCalendar
          value={value?.isValid() ? value : null}
          onChange={(date: Dayjs | null, selectionState) => {
            // Picking a year only moves the calendar on to that year's days; the day picks.
            if (selectionState !== 'finish') return;
            setCalendarAnchor(null);
            commit(date);
          }}
        />
      </Popover>
    </>
  );
};

export default DatePicker;
